/**
 * Let staff enrol a child from a class page.
 *
 * Clicking Register on a class used to tell owners and admins "Only parents
 * can register for classes" and stop there. The guard existed for a real
 * reason: parent checkout bills the person who is logged in, so sending an
 * owner through it would attach the order, the Stripe customer and the card
 * to the owner rather than to the family.
 *
 * So staff do not go to checkout. They get the admin enrolment form, which
 * already exists for exactly this: pick any family's child, set the price,
 * and optionally charge that family's own saved card. This hook is the
 * single copy of that behaviour, shared by every page with a Register
 * button.
 *
 * Usage:
 *   const staffEnrolment = useStaffEnrollment();
 *   if (staffEnrolment.isStaff) return staffEnrolment.open(classId, childId);
 *   ...
 *   {staffEnrolment.modal}
 */

import React, { useCallback, useMemo, useState } from "react";

import EnrollmentFormModal from "../components/admin/EnrollmentFormModal";
import { useAuth } from "../context/auth";

export default function useStaffEnrollment(onEnrolled) {
  const { user } = useAuth();
  // { classId, childId } while the form is open. The child is set when
  // staff started from "Add Class" on a family's child.
  const [target, setTarget] = useState(null);

  const role = user?.role?.toUpperCase();
  const isStaff = role === "OWNER" || role === "ADMIN";

  const open = useCallback(
    (classId, childId) =>
      setTarget(classId ? { classId, childId: childId || null } : null),
    [],
  );
  const close = useCallback(() => setTarget(null), []);

  const modal = useMemo(
    () =>
      target ? (
        <EnrollmentFormModal
          isOpen
          mode="create"
          // Seeds the class (and the child, when known) so staff only
          // confirm the details.
          initialData={
            target.childId
              ? { class_id: target.classId, child_id: target.childId }
              : { class_id: target.classId }
          }
          onClose={close}
          onSuccess={() => {
            close();
            if (onEnrolled) onEnrolled();
          }}
        />
      ) : null,
    [target, close, onEnrolled],
  );

  return { isStaff, open, close, modal };
}
